import { defaultHomeBattlesCopy, HOME_BATTLES_LIMIT } from "@loot-os/api-contract";
import type { HomeBattlesContent } from "@loot-os/design-system/templates/home/homeContent";
import { useQuery } from "@tanstack/react-query";

import { battleVisualStatus } from "../../../../lib/battleTimeline.js";
import { battlesQueryOptions } from "../../../../lib/queries.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTenant } from "../../../../lib/tenant.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { useBattleCard } from "../../battles/shared/useBattleCard.js";

/** Open battles first, then running ones, then the latest finished to fill the rest. */
const ORDER = ["joinable", "in-progress", "finished"] as const;

/**
 * Home's battles: the lobby's first few (GET /v1/battles), open ones, then running ones, then the
 * latest finished (faded, as the lobby shows them), each the lobby's card. Left out while the
 * store has battles off, and while the lobby loads or fails, so Home never waits on it; `[]`
 * cards when the store has no battles at all.
 */
export function useHomeBattlesContent(): HomeBattlesContent | undefined {
  const { hasFeature } = useTenant();
  const { user, sessionToken } = useShopper();
  const terms = useTerminology();
  const on = hasFeature("battles");
  const lobby = useQuery({
    ...battlesQueryOptions({ viewer: sessionToken && user ? user.id : "anon", poll: false }),
    enabled: on,
  });
  const card = useBattleCard();
  if (!on || !lobby.data) return undefined;
  const battles = ORDER.flatMap((status) =>
    lobby.data.data.filter((battle) => battleVisualStatus(battle) === status),
  ).slice(0, HOME_BATTLES_LIMIT);
  const copy = defaultHomeBattlesCopy(terms);
  return {
    title: copy.title,
    viewAll: { label: copy.viewAll, href: "/battles" },
    cards: battles.map(card),
  };
}
